import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';

import { SecurityContext } from 'utils';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';
import Constants from 'constants/Constants';
import Config from 'constants/Config';

import Listener from 'PMT/flux/Listener';

// -- stores
import { RestaurantStore, OrderSettingsStore } from 'stores';

import {
  RouteAction,
  SessionAction,
  OrderAction,
  RestaurantAction
} from 'actions';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';

import { OrderSettings } from 'PMT/entities/order';

// -- views
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import { Tabs, Tab } from 'react-bootstrap';

import OrderGeneralSettingsPage from './components/OrderGeneralSettingsPage';
import OrderOpeningHoursSettingsPage from './components/OrderOpeningHoursSettingsPage';

import OnSiteSettingsPage from './modes/OnSiteSettingsPage';
import TakeAwaySettingsPage from './modes/TakeAwaySettingsPage';
import DeliverySettingsPage from './modes/DeliverySettingsPage';

import KioskSettingsPage from './kiosk/KioskSettingsPage';
import ApiErrorView from 'components/ApiErrorView'

export default class OrdersSettingsPage extends AbstractComponent {

  static Tabs = {
    GENERAL: 'general',
    OPENING_HOURS: 'opening-hours',
    KIOSK: 'kiosk',
    ON_SITE: 'on-site',
    TAKE_AWAY: 'take-away',
    DELIVERY: 'delivery',
  };

  constructor() {
    super();

    this.arianeIcon = 'fa-line-chart';
    this.arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: RoutingEnum.ORDERS_LIST
      },
      {
        name: I18n.tr('menu.order.settings'),
        link: null
      }
    ];
  }

  state = {
    restaurant: null,
    orderSettings: null
  };

  componentDidMount() {
    if (!RestaurantStore.getRestaurant().haveFeatureOrder()) {
      RouteAction.redirectToFeatureNonActivated();
      return;
    }

    if (!SecurityContext.orderSettings.canRead()) {
      RouteAction.redirectForce(RoutingEnum.ORDERS_LIST);
      return;
    }

    RestaurantStore.addListeners(this.restaurantStoreListerners);
    OrderSettingsStore.addListeners(this.orderStoreListerners);

    this.loadCurrentRestaurant();
  }

  componentWillUnmount() {
    RestaurantStore.removeListeners(this.restaurantStoreListerners);
    OrderSettingsStore.removeListeners(this.orderStoreListerners);
  }

  get restaurantStoreListerners(): Array<Listener> {
    return [
      new Listener(Events.LOAD_RESTAURANT_SUCCESS, this.onChange),
      new Listener(Events.UPDATE_RESTAURANT_SUCCESS, this.onChange),
      new Listener(Events.LOAD_RESTAURANT_FAILURE, this.onError),
    ];
  }

  get orderStoreListerners(): Array<Listener> {
    return [
      new Listener(Events.ON_UPDATE_ORDER_SETTINGS_SUCCESS, this.onUpdateOrderSettings),
      new Listener(Events.ON_UPDATE_ORDER_SETTINGS_FAILURE, this.onUpdateOrderSettingsError),
    ];
  }

  loadCurrentRestaurant = () => {
    this.showLoading();
    RestaurantAction.retrieveCurrentRestaurant();
  };


  //
  // events - API Restaurant
  //

  onChange = () => {
    const restaurant: Restaurant = RestaurantStore.getRestaurant();

    this.setState({
      restaurant,
      orderSettings: restaurant.orderSettings,
      loading: false
    });
  };

  onError = () => {
  };


  //
  // Events OrderSettings
  //

  onUpdateOrderSettings = () => {
    const orderSettings = OrderSettingsStore.getOrderSettings();

    // update, just in case.
    const restaurant: Restaurant = RestaurantStore.getRestaurant();
    restaurant.orderSettings = orderSettings;

    this.setState({
      orderSettings,
      restaurant,
      loading: false
    });
  };

  onUpdateOrderSettingsError = () => {
    // const orderSettings = OrderSettingsStore.getOrderSettings();

    //  // update, just in case.
    //  const restaurant: Restaurant = RestaurantStore.getRestaurant();
    //  restaurant.orderSettings = orderSettings;

    const apiError = OrderSettingsStore.getApiError();
    this.setState({
      apiError,
      loading: false
    })
  };


  //
  // Tabs
  //

  onTabChange = (activeKey) => {
    if (!_.isUndefined(activeKey)) {
      /*
       * Update the query params.
       */
      this.onChangeQueryUrl({
        tab: activeKey
      });
    }
  };

  get defaultTabKey(): number {
    if (!_.isNull(this.props.location.query)) {
      const tabQuery = this.props.location.query.tab;
      return tabQuery;
    }

    return null;
  }

  //
  // events
  //

  onSubmit = (orderSettings: OrderSettings) => {
    this.showLoading();
    OrderAction.updateOrderSettings(orderSettings);
  };

  //
  // renderers
  //

  renderWarnings() {
    return (
      <div>
        {this.state.restaurant && this.state.restaurant.status === 'ARCHIVED' && (
          <p className="bg-warning" style={{ padding: 16, marginTop: 16, }}>{I18n.tr('restaurant_edit.configuration.statut.ARCHIVED.tip')}</p>
        )}
      </div>
    )
  }

  renderGeneral() {
    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.GENERAL}
        title={I18n.tr('order.settings.tabs.general').toUpperCase()}
      >
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <OrderGeneralSettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }

  renderOpeningHours() {
    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.OPENING_HOURS}
        title={I18n.tr('order.settings.tabs.opening_hours').toUpperCase()}
      >
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <OrderOpeningHoursSettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }

  renderOnSite() {
    // if (!this.state.orderSettings.onSiteSettings.enabled) {
    //   return (null);
    // }

    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.ON_SITE}
        title={I18n.tr('order.settings.tabs.on_site').toUpperCase()}
      >        
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <OnSiteSettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }

  renderTakeAway() {
    // if (!this.state.orderSettings.takeAwaySettings.enabled) {
    //   return (null);
    // }

    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.TAKE_AWAY}
        title={I18n.tr('order.settings.tabs.take_away').toUpperCase()}
      >
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <TakeAwaySettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }

  renderDelivery() {
    // if (!this.state.orderSettings.deliverySettings.enabled) {
    //   return (null);
    // }

    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.DELIVERY}
        title={I18n.tr('order.settings.tabs.delivery').toUpperCase()}
      >
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <DeliverySettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }


  renderKiosk() {
    return (
      <Tab
        eventKey={OrdersSettingsPage.Tabs.KIOSK}
        title={I18n.tr('order.settings.tabs.kiosk').toUpperCase()}
      >
        {this.renderWarnings()}
        <div className="top-buffer-30">
          <KioskSettingsPage
            restaurant={this.state.restaurant}
            orderSettings={this.state.orderSettings}
            onSubmit={this.onSubmit}
          />
        </div>
      </Tab>
    );
  }

  renderBody() {
    if (_.isNull(this.state.restaurant)) {
      return this.renderOnLoadingContent()
    }

    return (
      <Tabs
        defaultActiveKey={this.defaultTabKey}
        id="order_settings__tabs"
        onSelect={this.onTabChange}
      >
        {this.renderGeneral()}
        {this.renderOpeningHours()}
        {this.renderKiosk()}

        {this.renderOnSite()}
        {this.renderTakeAway()}
        {this.renderDelivery()}
      </Tabs>
    );
  }

  render() {
    return (
      <div className="order_settings_page">
        <Ariane icon={this.arianeIcon} details={this.arianeDetails} />

        {this.renderLoading()}

        <ApiErrorView apiError={this.state.apiError} />
        
        {this.renderBody()}
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/order/settings/OrderSettingsPage.js